/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionsRevokeAll } from "../funcs/organizationRemoteSessionsRevokeAll.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RevokeAllRemoteSessionsResult } from "../models/components/revokeallremotesessionsresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevokeAllOrganizationRemoteSessionClientSessionsRequest,
  RevokeAllOrganizationRemoteSessionClientSessionsSecurity,
} from "../models/operations/revokeallorganizationremotesessionclientsessions.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RevokeAllOrganizationRemoteSessionClientSessionsMutationVariables =
  {
    request: RevokeAllOrganizationRemoteSessionClientSessionsRequest;
    security?:
      | RevokeAllOrganizationRemoteSessionClientSessionsSecurity
      | undefined;
    options?: RequestOptions;
  };

export type RevokeAllOrganizationRemoteSessionClientSessionsMutationData =
  RevokeAllRemoteSessionsResult;

export type RevokeAllOrganizationRemoteSessionClientSessionsMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revokeAllClientSessions organizationRemoteSessions
 *
 * @remarks
 * Revoke (soft-delete) all remote_sessions minted against a remote_session_client in the caller's organization. Requires org:admin.
 */
export function useRevokeAllOrganizationRemoteSessionClientSessionsMutation(
  options?: MutationHookOptions<
    RevokeAllOrganizationRemoteSessionClientSessionsMutationData,
    RevokeAllOrganizationRemoteSessionClientSessionsMutationError,
    RevokeAllOrganizationRemoteSessionClientSessionsMutationVariables
  >,
): UseMutationResult<
  RevokeAllOrganizationRemoteSessionClientSessionsMutationData,
  RevokeAllOrganizationRemoteSessionClientSessionsMutationError,
  RevokeAllOrganizationRemoteSessionClientSessionsMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRevokeAllOrganizationRemoteSessionClientSessionsMutation(
      client,
      options,
    ),
    ...options,
  });
}

export function mutationKeyRevokeAllOrganizationRemoteSessionClientSessions(): MutationKey {
  return ["@gram/client", "organizationRemoteSessions", "revokeAll"];
}

export function buildRevokeAllOrganizationRemoteSessionClientSessionsMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables:
      RevokeAllOrganizationRemoteSessionClientSessionsMutationVariables,
  ) => Promise<RevokeAllOrganizationRemoteSessionClientSessionsMutationData>;
} {
  return {
    mutationKey: mutationKeyRevokeAllOrganizationRemoteSessionClientSessions(),
    mutationFn:
      function revokeAllOrganizationRemoteSessionClientSessionsMutationFn({
        request,
        security,
        options,
      }): Promise<
        RevokeAllOrganizationRemoteSessionClientSessionsMutationData
      > {
        const mergedOptions = {
          ...hookOptions,
          ...options,
          fetchOptions: {
            ...hookOptions?.fetchOptions,
            ...options?.fetchOptions,
            signal: combineSignals(
              hookOptions?.fetchOptions?.signal,
              options?.fetchOptions?.signal,
            ),
          },
        };
        return unwrapAsync(organizationRemoteSessionsRevokeAll(
          client$,
          request,
          security,
          mergedOptions,
        ));
      },
  };
}
